import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Ear, Mic, MicOff, RefreshCw, Settings2, Square } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { alturaDoNivel } from '@/components/voice/MicLevelGraph';
import { nivelDaLinha, useOuvirMicrofones } from '@/components/voice/useOuvirMicrofones';
import { CHAVE_PADRAO, useMicrofonesAbertos, type EstadoMic, type MicAberto, type UsoMic } from '@/services/voice/mics/ativos';
import { listMics, onMicsChange, type MicDevice } from '@/services/voice/mics/devices';
import { corteDb, dbParaGanho, paraDb } from '@/services/voice/mics/dsp';
import { DetectorFalante, LIMIAR_FALANTE_PADRAO_DB, type Falante } from '@/services/voice/mics/falante';
import { rotuloDe } from '@/services/voice/mics/index';
import { misturaSuportada } from '@/services/voice/mics/mixer';
import { micPadrao, resolveMics, separaFalante, useMicsPrefs, type MicConfig, type Quem } from '@/services/voice/mics/store';

const USO: Record<UsoMic, string> = { escuta: 'escuta Jarvis/Atlas', ditado: 'ditado', gravacao: 'gravação' };

const ESTADO: Record<EstadoMic, string> = {
  falando: 'captando voz',
  quieto: 'aberto, em silêncio',
  aberto: 'aberto',
  caiu: 'NÃO está captando (não abriu ou foi desligado)',
};

const QUEM: Record<Quem, string> = { medico: 'Médico', paciente: 'Paciente', ambos: 'Os dois' };

/** "Mais alto agora" no teste: acima disto e com folga sobre o segundo (igual a Configurações). */
const DESTAQUE_MIN_DB = -45;
const DESTAQUE_FOLGA_DB = 6;

/** O microfone padrão do Windows, quando "Vários microfones" está desligado. */
const PADRAO: MicDevice = { id: 'default', nome: 'Microfone padrão do Windows', virtual: false };

interface Linha {
  chave: string;
  rotulo: string;
  nome: string;
  /** Aparelho pro medidor do "Ouvir"; sem = desligado do computador. */
  dev?: MicDevice;
  cfg: MicConfig;
  emUso: boolean;
}

/**
 * Como está o microfone pro ponto da barra: no teste, o medidor; fora dele,
 * a captura aberta (ditado, gravação, escuta); senão, parado.
 */
type Ponto =
  | { tipo: 'teste'; nivel: number; acimaDoCorte: boolean }
  | { tipo: 'captura'; m: MicAberto }
  | { tipo: 'parado' }
  | { tipo: 'desligado' };

const PontoMic = ({ p }: { p: Ponto }) => {
  if (p.tipo === 'desligado' || (p.tipo === 'captura' && p.m.estado === 'caiu')) {
    return <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-error" aria-hidden />;
  }
  if (p.tipo === 'parado') {
    return <span className="h-2.5 w-2.5 shrink-0 rounded-full border-2 border-on-surface-variant/50" aria-hidden />;
  }
  const falando = p.tipo === 'teste' ? p.acimaDoCorte : p.m.estado === 'falando';
  const nivel = p.tipo === 'teste' ? p.nivel : p.m.nivel;
  const halo = falando ? 2 + Math.min(4, nivel * 40) : 0;
  return (
    <span
      className={`h-2.5 w-2.5 shrink-0 rounded-full bg-success transition-[box-shadow,opacity] duration-150 ${falando ? '' : 'opacity-60'}`}
      style={halo ? { boxShadow: `0 0 0 ${halo.toFixed(1)}px rgb(var(--c-success) / 0.25)` } : undefined}
      aria-hidden
    />
  );
};

function textoPonto(p: Ponto): string {
  switch (p.tipo) {
    case 'teste':
      return p.acimaDoCorte ? 'teste: captando som' : 'teste: em silêncio';
    case 'captura':
      return `${ESTADO[p.m.estado]} · ${p.m.usos.map((u) => USO[u]).join(', ')}`;
    case 'parado':
      return 'parado (nada gravando agora)';
    case 'desligado':
      return 'desligado do computador';
  }
}

/**
 * Microfones na barra do topo (spec 016; até 28/10/2026 era um chip no
 * rodapé do menu da esquerda). Um ponto por microfone em uso — com
 * "Vários microfones" ligado, um por aparelho marcado "Em uso" (Lapela,
 * Mesa…); desligado, o microfone padrão do Windows. O ponto mostra a
 * captura aberta agora (verde com halo = captando voz, vermelho = caiu ou
 * desligado do computador, vazio = nada gravando).
 *
 * O clique abre o "Ouvir os microfones" ali mesmo: medidor ao vivo de cada
 * um, o mais alto agora e quem fala (lapela × sala), sem ir até
 * Configurações. Quem/onde, volume e corte seguem em Configurações › Ditado
 * por voz › Microfones (link no fim).
 *
 * `compacto`: só os pontos, sem os nomes (barra apertada).
 */
export const MicsNoTopo = ({ compacto = false }: { compacto?: boolean }) => {
  const navigate = useNavigate();
  const prefs = useMicsPrefs();
  const abertos = useMicrofonesAbertos();
  const [devs, setDevs] = useState<MicDevice[] | null>(null);
  const [aberto, setAberto] = useState(false);
  const [ouvindo, setOuvindo] = useState(false);
  const [verOutros, setVerOutros] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const suportada = useMemo(() => misturaSuportada(), []);
  const caixaRef = useRef<HTMLDivElement>(null);

  const atualizar = useCallback(async () => setDevs(await listMics()), []);
  useEffect(() => {
    void atualizar();
    return onMicsChange(() => void atualizar());
  }, [atualizar]);

  // Fechar o painel para o teste (o medidor segura os aparelhos). Fecha com
  // Esc ou clique fora — sem camada fixa: o desfoque da barra prenderia a
  // camada dentro dela.
  useEffect(() => {
    if (!aberto) {
      setOuvindo(false);
      setVerOutros(false);
      return;
    }
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && setAberto(false);
    const fora = (e: PointerEvent) => {
      if (!caixaRef.current?.contains(e.target as Node)) setAberto(false);
    };
    window.addEventListener('keydown', esc);
    document.addEventListener('pointerdown', fora);
    return () => {
      window.removeEventListener('keydown', esc);
      document.removeEventListener('pointerdown', fora);
    };
  }, [aberto]);

  const vario = prefs.ativo && suportada;
  const resolvidos = useMemo(() => resolveMics(prefs, devs ?? []), [prefs, devs]);

  const linhas = useMemo<Linha[]>(() => {
    if (!vario) {
      const cfg = { ...micPadrao(PADRAO), corte: 0 };
      return [{ chave: CHAVE_PADRAO, rotulo: 'Microfone padrão', nome: PADRAO.nome, dev: PADRAO, cfg, emUso: true }];
    }
    const out: Linha[] = resolvidos.map(({ dev, cfg }) => ({ ...rotuloDe(cfg), dev, cfg, emUso: cfg.usar }));
    // Marcados "Em uso" que não estão ligados agora (nem pelo nome).
    for (const cfg of prefs.mics) {
      if (!cfg.usar || resolvidos.some((r) => r.cfg.nome === cfg.nome || r.dev.id === cfg.id)) continue;
      out.push({ ...rotuloDe(cfg), cfg, emUso: true });
    }
    return out;
  }, [vario, resolvidos, prefs.mics]);

  const emUso = useMemo(() => linhas.filter((l) => l.emUso), [linhas]);
  const outros = useMemo(() => linhas.filter((l) => !l.emUso && l.dev && !l.dev.virtual), [linhas]);
  // Nenhum marcado: a mistura segue no padrão do Windows — testa os ligados.
  const nenhumEmUso = vario && emUso.length === 0;
  const visiveis = useMemo(() => (nenhumEmUso || verOutros ? [...emUso, ...outros] : emUso), [nenhumEmUso, verOutros, emUso, outros]);
  const semPermissao = devs !== null && (devs.length === 0 || devs.some((d) => d.semNome));

  const idsTeste = [...new Set(visiveis.flatMap((l) => (l.dev ? [l.dev.id] : [])))];
  const niveis = useOuvirMicrofones(idsTeste, ouvindo, () => setOuvindo(false));

  const pontoDe = (l: Linha): Ponto => {
    if (!l.dev) return { tipo: 'desligado' };
    if (ouvindo && niveis[l.dev.id]) {
      const nivel = nivelDaLinha(l.cfg, niveis[l.dev.id]);
      const limiar = corteDb(l.cfg.corte);
      return { tipo: 'teste', nivel, acimaDoCorte: paraDb(nivel) >= (limiar ?? DESTAQUE_MIN_DB) };
    }
    const m = abertos.find((a) => a.chave === l.chave);
    return m ? { tipo: 'captura', m } : { tipo: 'parado' };
  };

  // O mais alto agora, com folga sobre o segundo.
  const destaque = useMemo(() => {
    if (!ouvindo) return null;
    const ordem = visiveis
      .filter((l) => l.dev)
      .map((l) => ({ k: l.chave, db: paraDb(nivelDaLinha(l.cfg, niveis[l.dev!.id])) }))
      .sort((a, b) => b.db - a.db);
    const [a, b] = ordem;
    if (!a || a.db < DESTAQUE_MIN_DB) return null;
    if (b && a.db - b.db < DESTAQUE_FOLGA_DB) return null;
    return a.k;
  }, [ouvindo, visiveis, niveis]);

  // Quem fala (fase 2): o mesmo detector de Configurações, nos medidores do teste.
  const limiarFalante = prefs.limiarFalanteDb ?? LIMIAR_FALANTE_PADRAO_DB;
  const usadosLigados = emUso.filter((l) => l.dev);
  const separa = vario && separaFalante(usadosLigados.map((l) => l.cfg));
  const [falante, setFalante] = useState<Falante | null>(null);
  const [detector] = useState(() => new DetectorFalante(LIMIAR_FALANTE_PADRAO_DB));
  useEffect(() => {
    if (!ouvindo || !separa) return setFalante(null);
    detector.setLimiar(limiarFalante);
    let med = 0;
    let sala = 0;
    let voz = false;
    for (const l of usadosLigados) {
      const nivel = nivelDaLinha(l.cfg, niveis[l.dev!.id]);
      voz ||= paraDb(nivel) >= (corteDb(l.cfg.corte) ?? -60);
      if (l.cfg.quem === 'medico') med = nivel;
      else sala = Math.max(sala, nivel);
    }
    setFalante(detector.push(med, sala, voz, 0.1));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ouvindo, separa, niveis, limiarFalante]);

  const permitir = async () => {
    setErro(null);
    try {
      const s = await navigator.mediaDevices.getUserMedia({ audio: true });
      s.getTracks().forEach((t) => t.stop());
    } catch {
      setErro('Sem acesso ao microfone. Libere no cadeado da barra de endereço e tente de novo.');
    }
    await atualizar();
  };

  // A barra: os em uso (ou o padrão). Problema = algum caiu ou foi desligado.
  const naBarra = nenhumEmUso ? [] : emUso;
  const pontos = naBarra.map((l) => ({ l, p: pontoDe(l) }));
  const problema = pontos.some(({ p }) => p.tipo === 'desligado' || (p.tipo === 'captura' && p.m.estado === 'caiu'));
  const captando = pontos.some(({ p }) => p.tipo === 'captura' || p.tipo === 'teste');
  const title = [
    nenhumEmUso
      ? 'Vários microfones ligado, mas nenhum marcado "Em uso": segue no microfone padrão.'
      : `Microfones${vario ? ' em uso' : ''}:`,
    ...pontos.map(({ l, p }) => `${l.rotulo} — ${textoPonto(p)}`),
    'Clique pra ouvir os microfones.',
  ].join('\n');

  return (
    <div ref={caixaRef} className="relative shrink-0">
      <button
        type="button"
        onClick={() => setAberto((v) => !v)}
        aria-expanded={aberto}
        aria-haspopup="dialog"
        title={title}
        data-mics-topo
        className={`inline-flex items-center gap-2 h-9 px-3 rounded-full border text-[12px] font-medium whitespace-nowrap transition-colors ${
          problema
            ? 'border-error/40 bg-error/10 text-error hover:bg-error/15'
            : captando
              ? 'border-success/30 bg-success/10 text-on-surface hover:bg-success/15'
              : 'border-outline-variant/40 text-on-surface-variant hover:bg-surface-container hover:text-on-surface'
        }`}
      >
        {problema ? <MicOff size={15} aria-hidden /> : <Mic size={15} className={captando ? 'text-success' : ''} aria-hidden />}
        {pontos.length === 0 ? (
          <span className={compacto ? 'sr-only' : ''}>Microfones</span>
        ) : (
          pontos.map(({ l, p }) => (
            <span key={l.chave} className="inline-flex items-center gap-1.5 min-w-0">
              <PontoMic p={p} />
              <span className={compacto ? 'sr-only' : 'max-w-[8rem] truncate hidden 2xl:inline'}>{l.rotulo}</span>
            </span>
          ))
        )}
        <span className="sr-only">{pontos.map(({ l, p }) => `${l.rotulo}: ${textoPonto(p)}`).join('; ')}</span>
      </button>

      {aberto && (
          <div
            role="dialog"
            aria-label="Microfones"
            className="absolute right-0 top-full mt-2 z-50 w-[min(26rem,calc(100vw-2rem))] rounded-xl border border-outline-variant/30 bg-surface shadow-lg p-4 space-y-3"
          >
            <div>
              <div className="text-sm font-bold text-on-surface flex items-center gap-2">
                <Mic size={16} className="text-primary" /> Microfones
              </div>
              <p className="text-xs text-on-surface-variant mt-0.5">
                {!vario
                  ? 'Usando o microfone padrão do Windows.'
                  : nenhumEmUso
                    ? 'Nenhum microfone marcado "Em uso": segue no padrão do Windows.'
                    : `${emUso.length} ${emUso.length === 1 ? 'microfone em uso' : 'microfones em uso, juntos num só som'}.`}
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {ouvindo ? (
                <Button size="sm" variant="danger" className="gap-1.5" onClick={() => setOuvindo(false)}>
                  <Square size={14} /> Parar de ouvir
                </Button>
              ) : (
                <Button size="sm" variant="primary" className="gap-1.5" onClick={() => setOuvindo(true)} disabled={!suportada || idsTeste.length === 0}>
                  <Ear size={14} /> Ouvir os microfones
                </Button>
              )}
              <Button size="sm" variant="tertiary" className="gap-1.5" onClick={() => void atualizar()}>
                <RefreshCw size={14} /> Procurar de novo
              </Button>
              {semPermissao && (
                <Button size="sm" variant="secondary" onClick={() => void permitir()}>
                  Permitir o microfone
                </Button>
              )}
            </div>
            {!suportada && <p className="text-xs text-error">Este navegador não mostra o medidor (precisa do Chrome).</p>}
            {erro && <p className="text-xs text-error">{erro}</p>}

            {separa && ouvindo && (
              <p className="text-sm text-on-surface" aria-live="polite">
                Quem fala agora: <strong>{falante === 'medico' ? 'Médico' : falante === 'sala' ? 'Sala' : 'ninguém'}</strong>
              </p>
            )}

            <div className="space-y-2">
              {devs === null && <p className="text-sm text-on-surface-variant">Procurando…</p>}
              {visiveis.map((l) => {
                const p = pontoDe(l);
                const nivel = ouvindo && l.dev ? nivelDaLinha(l.cfg, niveis[l.dev.id]) : p.tipo === 'captura' ? p.m.nivel : 0;
                const limiar = vario ? corteDb(l.cfg.corte) : null;
                const abaixo = limiar !== null && paraDb(nivel) < limiar;
                const medindo = ouvindo && l.dev;
                return (
                  <div
                    key={l.chave}
                    className={`rounded-lg border px-3 py-2 space-y-1.5 ${destaque === l.chave ? 'border-primary' : 'border-outline-variant/50'} ${l.emUso ? '' : 'opacity-75'}`}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <PontoMic p={p} />
                      <span className="text-sm font-semibold text-on-surface truncate" title={l.nome}>{l.rotulo}</span>
                      {vario && l.emUso && (
                        <span className="shrink-0 rounded-full border border-outline-variant/60 px-2 text-[11px] text-on-surface-variant">{QUEM[l.cfg.quem]}</span>
                      )}
                      {!l.emUso && <span className="shrink-0 text-[11px] text-on-surface-variant">fora de uso</span>}
                      {destaque === l.chave && (
                        <span className="ml-auto shrink-0 rounded-full bg-primary px-2 text-[11px] text-on-primary">Mais alto agora</span>
                      )}
                    </div>
                    <div className="relative h-2.5 rounded bg-surface-container-high overflow-hidden" aria-hidden>
                      <div
                        className={`absolute inset-y-0 left-0 rounded ${abaixo ? 'bg-on-surface-variant/40' : 'bg-success'}`}
                        style={{ width: `${alturaDoNivel(nivel) * 100}%` }}
                      />
                      {limiar !== null && (
                        <div className="absolute inset-y-0 w-0.5 bg-on-surface/60" style={{ left: `${alturaDoNivel(dbParaGanho(limiar)) * 100}%` }} />
                      )}
                    </div>
                    <div className="text-[11px] text-on-surface-variant font-mono">
                      {medindo
                        ? niveis[l.dev!.id]
                          ? `${Number.isFinite(paraDb(nivel)) ? paraDb(nivel).toFixed(0) : '−∞'} dB`
                          : 'abrindo…'
                        : textoPonto(p)}
                    </div>
                  </div>
                );
              })}
            </div>

            {!nenhumEmUso && outros.length > 0 && (
              <Button size="sm" variant="tertiary" onClick={() => setVerOutros((v) => !v)}>
                {verOutros ? 'Esconder os fora de uso' : `Mostrar os outros ${outros.length} ${outros.length === 1 ? 'microfone' : 'microfones'}`}
              </Button>
            )}

            <p className="text-xs text-on-surface-variant">
              Fale normalmente: o seu microfone sobe. Bata de leve nos outros pra ver qual é qual. A risca é o corte de ruído.
            </p>
            <button
              type="button"
              onClick={() => {
                setAberto(false);
                navigate('/configuracoes?aba=voice-wake');
              }}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:underline"
            >
              <Settings2 size={13} /> Quem/onde, volume e corte: Configurações › Microfones
            </button>
          </div>
      )}
    </div>
  );
};
